import {
  Box,
  Button,
  CircularProgress,
  Link,
  TextField,
  Typography
} from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import LoginLanguageSwitcher from '../components/LoginLanguageSwitcher';
import BrandLogo from '../components/BrandLogo';
import { useBrand } from '../brand/BrandProvider';
import { authAPI } from '../services/api';
import '../styles/components/_login.scss';
import { AccountCircleIcon as UserIcon, EmailIcon } from '../utils/icons';
import { courseDashboardPathOrHome } from '../utils/studentNavigation';
import { getActiveLanguageCode } from '../utils/i18n';
import { extractBrandFromAuthResponse } from '../utils/applyAuthBrand';
import {
  DEFAULT_LOGIN_PICKER_LANGUAGES,
  normalizeLanguageList,
  pickInitialFormationLanguage,
  resolveInterfaceLanguage,
} from '../utils/loginLanguages';

type LoginStep = 'email' | 'code';

interface ScormContext {
  token: string;
  lmsUserId: string;
  firstName: string;
  lastName: string;
  language: string;
}

const pickFirstNonEmpty = (values: Array<string | null | undefined>): string => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return '';
};

const parseLearnerName = (rawName: string): { firstName: string; lastName: string } => {
  const trimmed = rawName.trim();
  if (!trimmed) return { firstName: '', lastName: '' };
  if (trimmed.includes(',')) {
    const [last, first] = trimmed.split(',', 2).map((s) => s.trim());
    return { firstName: first || '', lastName: last || '' };
  }
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length <= 1) return { firstName: parts[0] || '', lastName: '' };
  return {
    firstName: parts.slice(0, -1).join(' '),
    lastName: parts[parts.length - 1],
  };
};

interface ScormSettings {
  freeEnrollment: boolean;
  emailOptional: boolean;
}

const Login: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { setBrand } = useBrand();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState<LoginStep>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [isScormInitializing, setIsScormInitializing] = useState(false);
  const [isScormRedirecting, setIsScormRedirecting] = useState(false);
  const [error, setError] = useState('');
  const [scormFlowId, setScormFlowId] = useState<string>('');
  const [scormMaskedEmail, setScormMaskedEmail] = useState<string>('');
  const [scormSettings, setScormSettings] = useState<ScormSettings>({
    freeEnrollment: false,
    emailOptional: false
  });
  const [isScormFreeEnrollFlow, setIsScormFreeEnrollFlow] = useState<boolean>(false);
  /** Export SCORM désactivé / jeton périmé : écran minimal sans formulaire */
  const [scormUnavailableScreen, setScormUnavailableScreen] = useState(false);
  const [scormFormationLanguages, setScormFormationLanguages] = useState<string[] | null>(null);
  const [scormLanguagesReady, setScormLanguagesReady] = useState(false);
  const [learnerLanguage, setLearnerLanguage] = useState(() => getActiveLanguageCode());
  const scormInitRequestKeyRef = useRef<string>('');

  const persistAuthResponse = (response: {
    token: string;
    user: unknown;
    brand?: {
      primaryColor?: string;
      shellBackgroundColor?: string;
      logoUrl?: string | null;
    };
  }) => {
    localStorage.setItem('student_token', response.token);
    localStorage.setItem('student_user', JSON.stringify(response.user));
    const brand = extractBrandFromAuthResponse(response);
    if (brand) {
      setBrand(brand);
    }
  };

  const redirectAfterScormAuth = (formationId?: string) => {
    setIsScormRedirecting(true);
    navigate(courseDashboardPathOrHome(formationId), { replace: true });
  };

  const scormContext = useMemo<ScormContext | null>(() => {
    const token = pickFirstNonEmpty([
      searchParams.get('scormToken'),
      searchParams.get('token'),
      searchParams.get('scorm_token'),
    ]);
    const lmsUserId = pickFirstNonEmpty([
      searchParams.get('lmsUserId'),
      searchParams.get('learnerId'),
      searchParams.get('learner_id'),
      searchParams.get('studentId'),
      searchParams.get('student_id'),
      searchParams.get('userId'),
      searchParams.get('user_id'),
    ]);
    const rawLearnerName = pickFirstNonEmpty([
      searchParams.get('learnerName'),
      searchParams.get('learner_name'),
      searchParams.get('studentName'),
      searchParams.get('student_name'),
      searchParams.get('name'),
    ]);
    const parsedLearnerName = parseLearnerName(rawLearnerName);
    const firstName = pickFirstNonEmpty([
      searchParams.get('firstName'),
      searchParams.get('first_name'),
      searchParams.get('given_name'),
      parsedLearnerName.firstName,
    ]);
    const lastName = pickFirstNonEmpty([
      searchParams.get('lastName'),
      searchParams.get('last_name'),
      searchParams.get('family_name'),
      parsedLearnerName.lastName,
    ]);
    const language = pickFirstNonEmpty([
      searchParams.get('locale'),
      searchParams.get('language'),
      searchParams.get('lang'),
    ]);

    if (!token || !lmsUserId) {
      return null;
    }

    return {
      token,
      lmsUserId,
      firstName: firstName || 'Learner',
      lastName: lastName || 'SCORM',
      language: language || 'fr',
    };
  }, [searchParams]);

  const scormSessionKey = scormContext
    ? `${scormContext.token}:${scormContext.lmsUserId}`
    : '';

  const isScormFlow = Boolean(scormContext);
  const isCodeStep = step === 'code';
  const canScormSkipEmail = Boolean(
    scormContext && scormSettings.freeEnrollment && scormSettings.emailOptional
  );

  const loginLanguageOptions = scormContext
    ? (scormFormationLanguages ?? [])
    : [...DEFAULT_LOGIN_PICKER_LANGUAGES];

  const showLanguageSwitcher = scormContext
    ? scormLanguagesReady && !isScormRedirecting
    : true;

  const handleLoginLanguageChange = (lang: string) => {
    if (scormContext) {
      setLearnerLanguage(lang);
    }
    void i18n.changeLanguage(resolveInterfaceLanguage(lang));
  };

  const loginTitle = isScormFlow
    ? (isCodeStep ? t('login.scormTitleCode') : t('login.scormTitleEmail'))
    : (isCodeStep ? t('login.codeTitle') : t('login.title'));

  const loginDescription = isScormFlow
    ? (isCodeStep
      ? t('login.scormDescriptionCode', { email: scormMaskedEmail || email })
      : t('login.scormDescriptionEmail'))
    : (isCodeStep
      ? t('login.codeDescription', { email: scormMaskedEmail || email })
      : t('login.description'));

  const submitLabel = isScormFlow
    ? (isCodeStep ? t('login.scormValidateButton') : t('login.scormSendCodeButton'))
    : (isCodeStep ? t('login.validateButton') : t('login.sendCodeButton'));

  const normalizeErrorMessage = (value: unknown): string => {
    if (typeof value === 'string') {
      return value;
    }
    if (!value || typeof value !== 'object') {
      return '';
    }

    const candidate = value as Record<string, unknown>;
    const directMessage = candidate.message;
    if (typeof directMessage === 'string') {
      return directMessage;
    }
    const nestedError = candidate.error;
    if (typeof nestedError === 'string') {
      return nestedError;
    }
    const nestedResponse = candidate.errorResponse;
    if (nestedResponse && typeof nestedResponse === 'object') {
      const nestedResponseMessage = (nestedResponse as Record<string, unknown>).message;
      if (typeof nestedResponseMessage === 'string') {
        return nestedResponseMessage;
      }
    }

    // Last resort: stable string to avoid React trying to render objects.
    try {
      return JSON.stringify(candidate);
    } catch {
      return '';
    }
  };

  /** Texte erreur brute SCORM pour classification (priorité à `message` API prod). */
  const resolveScormBackendMessage = (err: unknown): string => {
    const e = err as { response?: { data?: Record<string, unknown> }; message?: string };
    const data = e?.response?.data;
    const apiErrorValue =
      (typeof data?.message === 'string' ? data.message : undefined) ??
      data?.error ??
      e?.message;
    return normalizeErrorMessage(apiErrorValue);
  };

  const isScormFormationUnavailableError = (err: unknown): boolean => {
    const rawError = resolveScormBackendMessage(err).toLowerCase();
    return (
      rawError.includes('inactive scorm export') ||
      rawError.includes('unknown or inactive scorm export token') ||
      rawError.includes('scorm token expired') ||
      rawError.includes('token expired')
    );
  };

  const getScormAuthErrorMessage = (err: any, fallback: string): string => {
    const normalizedMessage = resolveScormBackendMessage(err);
    const rawError = normalizedMessage.toLowerCase();
    if (
      rawError.includes('inactive scorm export') ||
      rawError.includes('unknown or inactive scorm export token') ||
      rawError.includes('scorm token expired') ||
      rawError.includes('token expired')
    ) {
      return t('login.scormUnavailable');
    }
    if (
      rawError.includes('invalid or expired code') ||
      rawError.includes('code invalide') ||
      rawError.includes('code expir')
    ) {
      return t('login.invalidOrExpiredCode');
    }
    if (rawError.includes('not enrolled')) {
      return t('login.scormNotEnrolled');
    }
    if (rawError.includes('email is required')) {
      return t('login.emailRequired');
    }
    if (rawError.includes('unable to continue without email')) {
      return t('login.scormSkipEmailError');
    }
    if (normalizedMessage && !rawError.includes('error')) {
      return normalizedMessage;
    }
    return fallback || t('login.scormUnexpectedError');
  };

  useEffect(() => {
    if (!scormContext) {
      setScormFormationLanguages(null);
      setScormLanguagesReady(true);
    }
  }, [scormSessionKey, scormContext]);

  useEffect(() => {
    if (!scormContext) {
      return;
    }

    authAPI.setScormSession({
      token: scormContext.token,
      lmsUserId: scormContext.lmsUserId,
    });
    void authAPI.ensureScormLmsLink('SCORM login tunnel started');
  }, [scormContext]);

  useEffect(() => {
    const bootstrapScormSession = async () => {
      if (!scormContext) {
        scormInitRequestKeyRef.current = '';
        return;
      }
      const requestKey = scormSessionKey;
      if (scormInitRequestKeyRef.current === requestKey) {
        return;
      }
      scormInitRequestKeyRef.current = requestKey;

      setIsScormInitializing(true);
      setLoading(true);
      setError('');
      setScormUnavailableScreen(false);
      setScormFormationLanguages(null);
      setScormLanguagesReady(false);

      try {
        const response = await authAPI.scormInitSession({
          token: scormContext.token,
          lmsUserId: scormContext.lmsUserId,
          firstName: scormContext.firstName,
          lastName: scormContext.lastName,
          language: scormContext.language
        });

        if (response.courseLanguages) {
          const available = normalizeLanguageList(response.courseLanguages.availableLanguages);
          const defaultLanguage = response.courseLanguages.defaultLanguage || 'en';
          setScormFormationLanguages(available);
          const initial = pickInitialFormationLanguage(
            scormContext.language,
            available,
            defaultLanguage
          );
          setLearnerLanguage(initial);
          void i18n.changeLanguage(resolveInterfaceLanguage(initial));
        }

        if (response.status === 'AUTO_LOGIN' && response.token && response.user) {
          persistAuthResponse({
            token: response.token,
            user: response.user,
          });
          redirectAfterScormAuth(response.formationId);
          return;
        }

        if (response.scormSettings) {
          setScormSettings(response.scormSettings);
        }

        if (response.status === 'MATCH_UNIQUE_EMAIL_SENT') {
          setScormFlowId(response.flowId || '');
          setScormMaskedEmail(response.maskedEmail || '');
          setIsScormFreeEnrollFlow(false);
          setStep('code');
          return;
        }

        setStep('email');
      } catch (err: any) {
        if (isScormFormationUnavailableError(err)) {
          setScormUnavailableScreen(true);
          setError('');
        } else {
          setScormUnavailableScreen(false);
          setError(getScormAuthErrorMessage(err, t('login.sendCodeError')));
        }
      } finally {
        setLoading(false);
        setIsScormInitializing(false);
        setScormLanguagesReady(true);
      }
    };

    bootstrapScormSession();
  }, [scormSessionKey, scormContext, navigate]);

  if (scormContext && scormUnavailableScreen) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'var(--brand-shell-bg)',
          boxSizing: 'border-box',
          px: 3,
        }}
      >
        <Typography
          variant="body1"
          sx={{
            color: '#FFFFFF',
            textAlign: 'center',
            maxWidth: 480,
            lineHeight: 1.6,
          }}
        >
          {t('login.scormUnavailable')}
        </Typography>
      </Box>
    );
  }

  if (scormContext && (isScormInitializing || isScormRedirecting)) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'var(--brand-shell-bg)',
        }}
      >
        <CircularProgress size={40} sx={{ color: 'var(--brand-primary)' }} />
      </Box>
    );
  }

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!email) {
      setError(t('login.emailRequired'));
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError(t('login.invalidEmail'));
      return;
    }

    setLoading(true);

    try {
      if (scormContext) {
        if (isScormFreeEnrollFlow) {
          const response = await authAPI.scormFreeEnroll({
            token: scormContext.token,
            lmsUserId: scormContext.lmsUserId,
            firstName: scormContext.firstName,
            lastName: scormContext.lastName,
            email,
            language: learnerLanguage
          });
          if (response.status === 'EMAIL_CODE_SENT') {
            setScormMaskedEmail(response.maskedEmail || email);
            setStep('code');
            return;
          }
        } else {
          const response = await authAPI.scormIdentifyByEmail({
            token: scormContext.token,
            lmsUserId: scormContext.lmsUserId,
            email,
            language: learnerLanguage
          });
          if (response.status === 'NOT_ENROLLED') {
            if (scormSettings.freeEnrollment) {
              setIsScormFreeEnrollFlow(true);
              const freeEnrollResponse = await authAPI.scormFreeEnroll({
                token: scormContext.token,
                lmsUserId: scormContext.lmsUserId,
                firstName: scormContext.firstName,
                lastName: scormContext.lastName,
                email,
                language: learnerLanguage
              });
              if (freeEnrollResponse.status === 'EMAIL_CODE_SENT') {
                setScormMaskedEmail(freeEnrollResponse.maskedEmail || email);
                setStep('code');
                return;
              }
            } else {
              setError(t('login.scormNotEnrolled'));
              return;
            }
          }
        }
      } else {
        await authAPI.sendCode(email, getActiveLanguageCode());
      }
      setStep('code');
    } catch (err: any) {
      setError(getScormAuthErrorMessage(err, t('login.sendCodeError')));
    } finally {
      setLoading(false);
    }
  };

  const handleCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!code) {
      setError(t('login.codeRequired'));
      return;
    }

    if (!/^\d{6}$/.test(code)) {
      setError(t('login.invalidCode'));
      return;
    }

    setLoading(true);

    try {
      const response = scormContext
        ? isScormFreeEnrollFlow
          ? await authAPI.scormFreeEnroll({
            token: scormContext.token,
            lmsUserId: scormContext.lmsUserId,
            firstName: scormContext.firstName,
            lastName: scormContext.lastName,
            email,
            code,
            language: learnerLanguage
          })
          : await authAPI.scormVerifyCode({
            token: scormContext.token,
            lmsUserId: scormContext.lmsUserId,
            code,
            flowId: scormFlowId || undefined,
            email: scormFlowId ? undefined : email,
            firstName: scormFlowId ? undefined : scormContext.firstName,
            lastName: scormFlowId ? undefined : scormContext.lastName
          })
        : await authAPI.verifyCode(email, code, getActiveLanguageCode());

      persistAuthResponse(response);

      // Redirect to dashboard
      if (scormContext) {
        void redirectAfterScormAuth(response.formationId);
      } else {
        navigate('/');
      }
    } catch (err: any) {
      setError(getScormAuthErrorMessage(err, t('login.invalidOrExpiredCode')));
    } finally {
      setLoading(false);
    }
  };

  const handleScormSkipEmail = async () => {
    if (!scormContext || !scormSettings.freeEnrollment || !scormSettings.emailOptional) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await authAPI.scormFreeEnroll({
        token: scormContext.token,
        lmsUserId: scormContext.lmsUserId,
        firstName: scormContext.firstName,
        lastName: scormContext.lastName,
        skipEmail: true,
        language: learnerLanguage
      });

      persistAuthResponse(response);
      void redirectAfterScormAuth(response.formationId);
    } catch (err: any) {
      setError(getScormAuthErrorMessage(err, t('login.scormSkipEmailError')));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box className="login">
      {/* Left Panel - Login Form */}
      <Box className="login__left-panel">
        {/* Header */}
        <Box className="login__header">
          <Box className="login__logo-container">
            <BrandLogo
              markClassName="login__logo-mark"
              markImageClassName="login__logo-mark-image"
              wordmarkClassName="login__logo-text"
            />
          </Box>
        </Box>

        {/* Main Content */}
        <Box className="login__content">
          {/* Icon */}
          <Box className="login__icon-container">
            {step === 'email' ? (
              <UserIcon size={80} className="login__icon" aria-hidden />
            ) : (
              <EmailIcon size={80} className="login__icon" aria-hidden />
            )}
          </Box>

          {/* Title */}
          <p className="login__title">{loginTitle}</p>

          {/* Description */}
          <p className="login__description">{loginDescription}</p>

          {/* Form */}
          <Box
            component="form"
            key={step}
            onSubmit={step === 'email' ? handleEmailSubmit : handleCodeSubmit}
            className="login__form"
          >
            {step === 'email' ? (
              <TextField
                fullWidth
                type="email"
                autoComplete='off'
                placeholder={t('login.emailPlaceholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="login__input"
                error={!!error}
              />
            ) : (
              <TextField
                fullWidth
                type="text"
                autoComplete='off'
                placeholder={t('login.codePlaceholder')}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                inputProps={{ maxLength: 6 }}
                className="login__input"
                error={!!error}
              />
            )}

            {error && (
              <Typography className="login__error">
                {error}
              </Typography>
            )}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              className="button-primary login__button"
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                submitLabel
              )}
            </Button>

            {canScormSkipEmail && (
              <Link
                component="button"
                type="button"
                underline="hover"
                disabled={loading}
                onClick={handleScormSkipEmail}
                className="login__skip-email-link"
              >
                {t('login.scormSkipEmailButton')}
              </Link>
            )}

          </Box>

          {/* Footer Text */}
          {step === 'email' && (
            <Box className="login__footer">
              <p className="login__footer-text">
                <span>{t('login.termsText')}</span>{' '}
                <Link
                  href={t('login.termsUrl')}
                  className="login__footer-link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span>{t('login.termsLink')}</span>
                </Link>
                <span>{t('login.termsTextEnd')}</span>
              </p>
            </Box>
          )}

          {step === 'code' && (
            <p className="login__code-help">
              {t('login.spamHelp')}
            </p>
          )}

          {showLanguageSwitcher && (
            <LoginLanguageSwitcher
              languages={loginLanguageOptions}
              selectedLanguage={scormContext ? learnerLanguage : undefined}
              onChange={handleLoginLanguageChange}
              visible={showLanguageSwitcher}
            />
          )}
        </Box>
      </Box>

      {/* Right Panel - Hero Image */}
      <Box className="login__right-panel">
        <img
          src={`${process.env.PUBLIC_URL || ''}/login-hero.png`}
          alt="Login hero"
          className="login__hero-image"
        />
      </Box>
    </Box>
  );
};

export default Login;
